import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { noop } from 'lodash';

type ReorderModeContextValue = {
  isReorderMode: boolean;
  setReorderMode: (value: boolean) => void;
};

const ReorderModeContext = createContext<ReorderModeContextValue | undefined>(undefined);

export const useReorderMode = () => {
  const context = useContext(ReorderModeContext);
  return useMemo(
    () => ({
      isReorderMode: context?.isReorderMode ?? false,
      setReorderMode: context?.setReorderMode ?? noop,
    }),
    [context?.isReorderMode, context?.setReorderMode],
  );
};

type Props = {
  children: React.ReactNode;
};

export const ReorderModeProvider = ({ children }: Props) => {
  const router = useRouter();
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize state from URL param on mount (only once when router is ready)
  useEffect(() => {
    if (router.isReady && !hasInitialized) {
      const sourceParam = router.query.source;
      if (sourceParam === 'reorder') {
        setIsReorderMode(true);
        // Remove the param from URL after consuming it (shallow update)
        // Use asPath to preserve the resolved market prefix and avoid double-prefixing
        const currentPath = router.asPath.split('?')[0];
        const urlSearchParams = new URLSearchParams(router.asPath.split('?')[1] || '');
        urlSearchParams.delete('source');
        const queryString = urlSearchParams.toString();
        const newUrl = queryString ? `${currentPath}?${queryString}` : currentPath;
        router.replace(newUrl, undefined, { shallow: true });
      }
      setHasInitialized(true);
    }
  }, [router, hasInitialized]);

  const setReorderMode = useCallback((value: boolean) => {
    setIsReorderMode(value);
  }, []);

  return (
    <ReorderModeContext.Provider value={{ isReorderMode, setReorderMode }}>
      {children}
    </ReorderModeContext.Provider>
  );
};
